import { useState, useEffect } from 'react';
import { supabase } from './supabase';

const DB_NAME = 'TripGenesysOffline';
const DB_VERSION = 1;
const STORES = {
  trips: 'trips',
  locations: 'locations',
  days: 'days',
  items: 'items',
  pendingSync: 'pendingSync',
};

export interface PendingChange {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: number;
}

class OfflineManager {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        Object.values(STORES).forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'id' });
          }
        });
      };
    });
  }

  async saveTrip(trip: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.trips], 'readwrite');
      const store = transaction.objectStore(STORES.trips);
      const request = store.put(trip);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getTrip(id: string): Promise<any> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.trips], 'readonly');
      const store = transaction.objectStore(STORES.trips);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllTrips(): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.trips], 'readonly');
      const store = transaction.objectStore(STORES.trips);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async addPendingChange(change: Omit<PendingChange, 'id' | 'timestamp'>): Promise<void> {
    if (!this.db) await this.init();

    const fullChange: PendingChange = {
      ...change,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.pendingSync], 'readwrite');
      const store = transaction.objectStore(STORES.pendingSync);
      const request = store.put(fullChange);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingChanges(): Promise<PendingChange[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.pendingSync], 'readonly');
      const store = transaction.objectStore(STORES.pendingSync);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearPendingChange(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.pendingSync], 'readwrite');
      const store = transaction.objectStore(STORES.pendingSync);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async syncPendingChanges(): Promise<{ success: number; failed: number }> {
    const changes = await this.getPendingChanges();
    let success = 0;
    let failed = 0;

    for (const change of changes) {
      try {
        switch (change.operation) {
          case 'insert':
            await supabase.from(change.table).insert(change.data);
            break;
          case 'update':
            await supabase.from(change.table).update(change.data).eq('id', change.data.id);
            break;
          case 'delete':
            await supabase.from(change.table).delete().eq('id', change.data.id);
            break;
        }

        await this.clearPendingChange(change.id);
        success++;
      } catch (error) {
        console.error('Failed to sync change:', error);
        failed++;
      }
    }

    return { success, failed };
  }

  async cacheTripsForOffline(tripIds: string[]): Promise<void> {
    for (const tripId of tripIds) {
      try {
        const { data: trip } = await supabase
          .from('trips')
          .select('*')
          .eq('id', tripId)
          .maybeSingle();

        if (trip) {
          await this.saveTrip(trip);
        }

        const { data: days } = await supabase
          .from('trip_days')
          .select('*')
          .eq('trip_id', tripId);

        const { data: items } = await supabase
          .from('trip_items')
          .select('*')
          .in(
            'trip_day_id',
            (days || []).map((d) => d.id)
          );

        if (days) {
          for (const day of days) {
            await this.saveData(STORES.days, day);
          }
        }

        if (items) {
          for (const item of items) {
            await this.saveData(STORES.items, item);
          }
        }
      } catch (error) {
        console.error(`Failed to cache trip ${tripId}:`, error);
      }
    }
  }

  private async saveData(storeName: string, data: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const offlineManager = new OfflineManager();

export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    const checkPending = async () => {
      const changes = await offlineManager.getPendingChanges();
      setPendingCount(changes.length);
    };

    checkPending();
    const interval = setInterval(checkPending, 5000);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      clearInterval(interval);
    };
  }, []);

  return { isOnline, pendingCount };
}

export async function syncWhenOnline(): Promise<void> {
  if (!navigator.onLine) {
    return;
  }

  try {
    const result = await offlineManager.syncPendingChanges();
  } catch (error) {
    console.error('Sync failed:', error);
  }
}

window.addEventListener('online', syncWhenOnline);
